import { votingIds } from "@artists/challenges-testkit"
import { getErrorCodes } from "@artists/devkit"
import { CustomErrorResponses } from "@artists/gql/design-challenges"
import { LoadingButton } from "@mui/lab"
import { useCallback } from "react"
import { useNavigate } from "react-router-dom"

import { useAuth } from "../../../../api"
import { useFatalErrors } from "../../../../api/hooks/useFatalError"
import type { Challenge } from "../../../../api/hooks/vote"
import { Root } from "../../../../utils"
import { FraudPrompt } from "../warnings"
import { useRecaptcha } from "./useRecaptcha"
import { useSubmit } from "./useSubmit"

interface Props {
	challenge: Challenge
	buttonText: string
	disabled: boolean
	onWarning: () => void
}

export const SubmitButton = ({
	challenge,
	buttonText,
	disabled,
	onWarning,
}: Props) => {
	const { slug, id, votingClosesAt } = challenge
	const navigate = useNavigate()
	const { onSubmit, isUpdating, error } = useSubmit({
		challengeId: id,
		slug,
	})

	useRecaptcha(onSubmit)

	const isVotingOpen =
		new Date(votingClosesAt).getTime() > new Date().getTime()
	const { authorized } = useAuth()

	const onButtonClick = useCallback(() => {
		if (!isVotingOpen) {
			onWarning()
			navigate(Root.Challenge.buildPath({ slug }))
		}

		if (!authorized) {
			window?.grecaptcha.execute()
		} else {
			void onSubmit()
		}
	}, [authorized, isVotingOpen, navigate, onSubmit, onWarning, slug])

	const errorCodes = error ? getErrorCodes(error) : []

	// This is an error we don't want to recover from
	useFatalErrors(errorCodes, [CustomErrorResponses.AccessDenied])

	return (
		<>
			<LoadingButton
				data-testid={votingIds.submitVotes}
				sx={{ textTransform: "capitalize", mb: 5 }}
				size='large'
				loading={isUpdating}
				variant='contained'
				disabled={disabled || !isVotingOpen}
				onClick={onButtonClick}
			>
				{buttonText}
			</LoadingButton>

			<div id='g-recaptcha' />

			<FraudPrompt
				slug={challenge.slug}
				withIpError={errorCodes.includes(CustomErrorResponses.IpError)}
				withCaptchaError={errorCodes.includes(
					CustomErrorResponses.CaptchaError,
				)}
			/>
		</>
	)
}
